import Phaser from 'phaser';

/**
 * Manages sequential announcement display to prevent overlapping UI.
 * Announcements are queued and processed one at a time with timeout protection.
 */
export class AnnouncementQueue {
  private scene: Phaser.Scene;
  private pending: (() => Promise<void>)[] = [];
  private isShowing: boolean = false;
  private elements: Phaser.GameObjects.GameObject[] = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Queue an announcement to be shown.
   * Will be processed in order after any currently showing announcements.
   */
  enqueue(showFn: () => Promise<void>): void {
    this.pending.push(showFn);
    this.processQueue();
  }

  /**
   * Track an announcement element for cleanup.
   */
  trackElement(element: Phaser.GameObjects.GameObject): void {
    this.elements.push(element);
  }

  /**
   * Remove an element from tracking (call when destroyed normally).
   */
  untrackElement(element: Phaser.GameObjects.GameObject): void {
    const idx = this.elements.indexOf(element);
    if (idx >= 0) {
      this.elements.splice(idx, 1);
    }
  }

  private async processQueue(): Promise<void> {
    if (this.isShowing || this.pending.length === 0) {
      return;
    }

    this.isShowing = true;
    const next = this.pending.shift();

    if (next) {
      try {
        // Timeout after 10 seconds to prevent deadlocks
        await Promise.race([
          next(),
          new Promise<void>((_, reject) => {
            this.scene.time.delayedCall(10000, () => reject(new Error('Announcement timeout')));
          }),
        ]);
      } catch (e) {
        console.warn('Announcement timed out or failed:', e);
      }
    }

    this.isShowing = false;
    this.processQueue();
  }

  /**
   * Clear all queued announcements and destroy visible elements.
   * Called on reconnection to prevent stale announcements.
   */
  clear(): void {
    for (const element of this.elements) {
      element.destroy();
    }
    this.elements = [];
    this.pending = [];
    this.isShowing = false;
  }

  get isShowingAnnouncement(): boolean {
    return this.isShowing;
  }

  get announcementElements(): Phaser.GameObjects.GameObject[] {
    return this.elements;
  }
}
